/**
 * Main-thread client for `src/workers/decodeWorker.ts` (owner requirement
 * 2026-09-26, requirement 4: "decode zlib/npy/npz in a Web Worker
 * (transferable ArrayBuffers)").
 *
 * `bytes` is copied (`.slice()`) before transfer rather than transferred
 * directly: `ArtifactStore` keeps the original buffer in its own raw-bytes
 * cache (`src/data/artifactStore.ts`) for reuse by a second decode of the
 * same artifact under a different codec path — a true zero-copy transfer
 * would detach that cached buffer out from under it. The copy is cheap
 * relative to the CPU work being moved off the main thread (zlib inflate,
 * npy/npz parsing of up to a 166,700-neuron array), which is the actual
 * point of this class.
 *
 * One worker per page session (lazily created on first use), reused for
 * every subsequent decode; `WorkerDecodeClient.isSupported()` lets callers
 * fall back to the synchronous in-thread decoders when `Worker` does not
 * exist (older browsers, and every non-browser test environment).
 */

import type { DecodeKind, DecodeRequest, DecodeResponse } from "../workers/decodeProtocol";

interface PendingCall {
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
}

export class WorkerDecodeClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private readonly pending = new Map<number, PendingCall>();

  static isSupported(): boolean {
    return typeof Worker !== "undefined";
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    const worker = new Worker(new URL("../workers/decodeWorker.ts", import.meta.url), {
      type: "module",
    });
    worker.addEventListener("message", (event: MessageEvent<DecodeResponse>) => {
      this.handleResponse(event.data);
    });
    worker.addEventListener("error", (event) => {
      // A worker-level error (e.g. a syntax error in the bundle) has no
      // request id to resolve — reject every still-pending call rather than
      // hanging callers forever.
      for (const [id, call] of this.pending) {
        call.reject(new Error(`decode worker error: ${event.message}`));
        this.pending.delete(id);
      }
    });
    this.worker = worker;
    return worker;
  }

  private handleResponse(response: DecodeResponse): void {
    const call = this.pending.get(response.id);
    if (!call) return;
    this.pending.delete(response.id);
    if (response.ok) call.resolve(response.result);
    else call.reject(new Error(response.error));
  }

  async decode<T>(kind: DecodeKind, bytes: ArrayBuffer, shape?: readonly number[]): Promise<T> {
    const worker = this.ensureWorker();
    const id = this.nextId++;
    const transferable = bytes.slice(0); // see module docstring: never transfer the caller's own buffer
    const request: DecodeRequest = { id, kind, bytes: transferable, shape };
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      worker.postMessage(request, [transferable]);
    });
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const [id, call] of this.pending) {
      call.reject(new Error("WorkerDecodeClient disposed"));
      this.pending.delete(id);
    }
  }
}

/** One shared client for the whole page session — matches `ArtifactStore`'s
 * own "one worker, reused" intent without every `RoleSnapshot`/
 * `ArtifactStore` instance spinning up its own. */
let sharedClient: WorkerDecodeClient | null = null;

export function getSharedDecodeClient(): WorkerDecodeClient | null {
  if (!WorkerDecodeClient.isSupported()) return null;
  sharedClient ??= new WorkerDecodeClient();
  return sharedClient;
}
